/** 한 줄 요소(input, button 등) 안의 텍스트가 보이는 너비에 맞게 rem 글자 크기 조절 */

const STEP_REM = 0.02;

export type ShrinkToFitFontOptions = {
  maxRem?: number;
  minRem?: number;
};

const DEFAULT_INPUT_MAX_REM = 0.82;
const DEFAULT_INPUT_MIN_REM = 0.45;

/**
 * 임의 HTMLElement + 표시할 문자열(내용과 동일한 문자열)로 측정해 `font-size` 조절.
 * 빈 문자열이면 인라인 font-size 제거.
 */
export function shrinkToFitElementFont(
  el: HTMLElement | null,
  text: string,
  options?: ShrinkToFitFontOptions
): void {
  if (!el) return;
  const maxRem = options?.maxRem ?? DEFAULT_INPUT_MAX_REM;
  const minRem = options?.minRem ?? DEFAULT_INPUT_MIN_REM;

  if (!text) {
    el.style.removeProperty('font-size');
    return;
  }

  const cs = getComputedStyle(el);
  const fontFamily = cs.fontFamily;
  const fontWeight = cs.fontWeight;
  const fontStyle = cs.fontStyle;
  const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const padL = parseFloat(cs.paddingLeft) || 0;
  const padR = parseFloat(cs.paddingRight) || 0;
  const widthAvail = el.clientWidth - padL - padR - 2;

  if (widthAvail <= 1) {
    el.style.fontSize = `${minRem}rem`;
    return;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    el.style.removeProperty('font-size');
    return;
  }

  const measure = (rem: number): number => {
    const fontSizePx = rem * rootPx;
    ctx.font = `${fontStyle} ${fontWeight} ${fontSizePx}px ${fontFamily}`;
    return ctx.measureText(text).width;
  };

  if (measure(maxRem) <= widthAvail) {
    el.style.removeProperty('font-size');
    return;
  }

  let rem = maxRem;
  while (rem > minRem && measure(rem) > widthAvail) {
    rem -= STEP_REM;
  }
  el.style.fontSize = `${Math.max(minRem, rem)}rem`;
}

/**
 * `input`의 `value`가 보이는 너비를 넘으면 `font-size`를 줄임. 맞으면 인라인 font-size 제거(CSS 기본).
 */
export function shrinkToFitInputFont(
  input: HTMLInputElement | null,
  options?: ShrinkToFitFontOptions
): void {
  if (!input) return;
  shrinkToFitElementFont(input, input.value, {
    maxRem: options?.maxRem ?? DEFAULT_INPUT_MAX_REM,
    minRem: options?.minRem ?? DEFAULT_INPUT_MIN_REM,
  });
}
